import { DoCheck, Input, OnInit, Directive } from '@angular/core';
import { UpdateComponent } from '../base/update.component';
import { BaseModel, HasId } from '../../models/base';
import { BaseHttpService, Query, UriBuilderParams } from '@app/core/services/base/base-http.service';
import { BaseList } from '../../models/list';
import * as _ from 'lodash-es';
import { DrQueryParams } from '@app/core/services/base/base-service-with-bundle.service';

export interface SelectComponentOptions {
  label?: string; // label to be used with the select input
  patchPath?: string; // needed for the patch method in the parent component
  class?: string; // optional - css classes to apply
  optional?: boolean; // optional - empty option in the list
  uriParams?: DrQueryParams; // Optional query parameters
  propOverride?: string; // Optional parameter to override property 'data'when the response of the server is not response.data
}

export interface DisplayName {
  displayName: string;
}

@Directive()
export abstract class BaseSelectWithIdComponent<T extends BaseModel & HasId & DisplayName, U extends UriBuilderParams<T>> extends UpdateComponent
                                                                                                                          implements OnInit, DoCheck {
  @Input()
  public selectedId: string;

  @Input()
  public disabled: boolean;

  @Input()
  protected options: SelectComponentOptions;

  protected defaultOptions: SelectComponentOptions;
  public mergedOptions: SelectComponentOptions;

  public list: HasId[] & DisplayName[];

  protected abstract generateUriBuilderParams(): U;

  protected constructor(protected drHttpService: BaseHttpService<T, U> & Query<T, U>) {
    super();
  }

  public ngOnInit() {
    super.ngOnInit();
    this.mergeOptions();
    this.fetchData();
  }

  public ngDoCheck() {
    const oldOptions = _.cloneDeep(this.mergedOptions);
    this.mergeOptions();

    if (this.executeNewQuery(oldOptions, this.mergedOptions)) {
      this.fetchData();
    }
  }

  protected mergeOptions(): void {
    this.mergedOptions = {};
    this.mergedOptions.label = (this.options && (this.options.label || this.options.label === '')) ? this.options.label : this.defaultOptions.label || '';
    this.mergedOptions.patchPath = this.options && this.options.patchPath || this.defaultOptions.patchPath;
    this.mergedOptions.class = this.options && this.options.class || this.defaultOptions.class;
    this.mergedOptions.optional = this.options && (this.options.optional === true || this.options.optional === false) ? this.options.optional : !!this.defaultOptions.optional;
    this.mergedOptions.uriParams = (this.options && this.options.uriParams) ? _.cloneDeep(this.options.uriParams) :  _.cloneDeep(this.defaultOptions.uriParams);
    this.mergedOptions.propOverride = this.options && this.options.propOverride || this.defaultOptions.propOverride || null;
  }

  // Override this method if the data needs to be fetched via a different way
  protected fetchData(): void {
    this.drHttpService.query(this.generateUriBuilderParams(), this.mergedOptions.uriParams, this.mergedOptions.propOverride)
                      .subscribe((result: BaseList<T>) => {
      const filtered = this.filterData(result.items);
      this.list = this.formatData(filtered);
    });
  }

  // Override this method if the data needs to be 'worked' after receiving it from the server
  protected formatData(data: HasId[] & DisplayName[]): HasId[] & DisplayName[] {
    return data;
  }

  // Override this method if the data needs to be 'filtered' after receiving it from the server
  protected filterData(data: HasId[] & DisplayName[]): HasId[] & DisplayName[] {
    return data;
  }

  protected executeNewQuery(oldOptions: SelectComponentOptions, newOptions: SelectComponentOptions): boolean {
    return newOptions && newOptions.uriParams &&
           oldOptions && !_.isEqual(oldOptions.uriParams, newOptions.uriParams);
  }

  public onSelectionChange(): void {
    this.onChange(this.mergedOptions.patchPath, this.selectedId);
  }
}
